import { reactive } from "vue";

import { getListingById } from "../data/query";
import { Listing, Preferences, QueryState } from "../data/types";

// 本地偏好（localStorage）：最近浏览、浏览量本地累加、上次筛选条件
// 无登录态、无接口调用；localStorage 不可用时静默降级为内存态
const STORAGE_KEY = "xiyou.prefs.v1";
const MAX_RECENT = 5;

const DEFAULT_QUERY: QueryState = { category: "", type: "", q: "", page: 1 };

function emptyPreferences(): Preferences {
  return { recentIds: [], viewOverrides: {}, lastQuery: { ...DEFAULT_QUERY } };
}

function readPreferences(): Preferences {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyPreferences();
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    const recentIds = Array.isArray(parsed.recentIds)
      ? parsed.recentIds.filter((x) => typeof x === "number")
      : [];
    const viewOverrides =
      parsed.viewOverrides && typeof parsed.viewOverrides === "object"
        ? parsed.viewOverrides
        : {};
    return {
      recentIds: recentIds.slice(0, MAX_RECENT),
      viewOverrides,
      lastQuery: { ...DEFAULT_QUERY, ...(parsed.lastQuery || {}) },
    };
  } catch {
    return emptyPreferences();
  }
}

export const preferences = reactive<Preferences>(readPreferences());

function persist(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // 隐私模式或存储被禁用：保持内存态即可，不影响页面
  }
}

/** 本地访问次数（叠加在基础浏览量之上） */
export function localViews(id: number): number {
  return preferences.viewOverrides[String(id)] || 0;
}

export function displayViews(base: number, id: number): number {
  return base + localViews(id);
}

export function rememberVisit(id: number): void {
  if (!getListingById(id)) return;
  preferences.viewOverrides[String(id)] = localViews(id) + 1;
  preferences.recentIds = [
    id,
    ...preferences.recentIds.filter((x) => x !== id),
  ].slice(0, MAX_RECENT);
  persist();
}

export function recentListings(): Listing[] {
  return preferences.recentIds
    .map((id) => getListingById(id))
    .filter((item): item is Listing => !!item);
}

export function rememberQuery(state: QueryState): void {
  preferences.lastQuery = { ...state };
  persist();
}

/** 上次筛选条件（仅在 URL 未带参数时用于兜底恢复） */
export function lastQuery(): QueryState {
  return { ...preferences.lastQuery };
}

export function clearPreferences(): void {
  const empty = emptyPreferences();
  preferences.recentIds = empty.recentIds;
  preferences.viewOverrides = empty.viewOverrides;
  preferences.lastQuery = empty.lastQuery;
  persist();
}
